/**
 * Audio the canvas upload API accepts. Its allowlist (api-app
 * canvas-file-upload.ts) holds only the canonical types, while browsers label
 * the same files with aliases (Chrome: `.m4a` is `audio/x-m4a`). Aliases of an
 * accepted type are relabelled to it before upload; anything else is refused
 * up front instead of leaving an empty node behind a failed upload.
 */

export const UPLOADABLE_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/mp4', 'audio/flac', 'audio/aac'] as const

/** Other names browsers and systems use for the same formats. */
const AUDIO_TYPE_ALIASES: Record<string, (typeof UPLOADABLE_AUDIO_TYPES)[number]> = {
  'audio/mp3': 'audio/mpeg',
  'audio/x-mp3': 'audio/mpeg',
  'audio/x-mpeg': 'audio/mpeg',
  'audio/x-wav': 'audio/wav',
  'audio/wave': 'audio/wav',
  'audio/vnd.wave': 'audio/wav',
  'audio/x-pn-wav': 'audio/wav',
  'audio/x-m4a': 'audio/mp4',
  'audio/m4a': 'audio/mp4',
  'audio/x-ogg': 'audio/ogg',
  'audio/opus': 'audio/ogg',
  'audio/x-flac': 'audio/flac',
  'audio/x-aac': 'audio/aac',
  'audio/aacp': 'audio/aac',
}

/** What the file picker offers, and what the empty node tells the user. */
export const UPLOADABLE_AUDIO_ACCEPT = [...UPLOADABLE_AUDIO_TYPES, ...Object.keys(AUDIO_TYPE_ALIASES), '.mp3', '.wav', '.ogg', '.oga', '.opus', '.m4a', '.flac', '.aac', '.weba'].join(',')
export const UPLOADABLE_AUDIO_LABEL = 'MP3, WAV, OGG, M4A, FLAC, AAC, WEBM'

function baseType(type: string): string {
  return type.split(';')[0].trim().toLowerCase()
}

/** The upload type for a browser-reported audio type, or null when the API refuses it (AIFF, ...). */
export function uploadableAudioType(type: string): string | null {
  const base = baseType(type)
  if ((UPLOADABLE_AUDIO_TYPES as readonly string[]).includes(base)) return base
  return AUDIO_TYPE_ALIASES[base] ?? null
}

/** The file ready to upload, relabelled when the browser used an alias; null when it can't be uploaded. */
export function toUploadableAudio(file: File): File | null {
  const type = uploadableAudioType(file.type)
  if (!type) return null
  return type === file.type ? file : new File([file], file.name, { type, lastModified: file.lastModified })
}

/** A short refusal naming the format, for a toast or the node. */
export function unsupportedAudioMessage(file: File): string {
  const ext = file.name.includes('.') ? file.name.split('.').pop()?.toUpperCase() : undefined
  return `${ext ?? 'This'} audio can't be uploaded yet. Use ${UPLOADABLE_AUDIO_LABEL}.`
}
